/**
 * "There is nothing here" — said in a way that distinguishes the reasons.
 *
 * An empty folder, a search with no matches and a list the caller cannot see
 * into all render zero rows, and they mean three different things. A bare blank
 * area leaves the user guessing which one they are looking at.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface EmptyStateProps {
  /** An icon element, usually from `@mui/icons-material`. */
  icon?: ReactNode;
  title: string;
  /** One line saying why it is empty, or what to do about it. */
  description?: string;
  /** A button, where there is an obvious next step. */
  action?: ReactNode;
  dense?: boolean;
}

export function EmptyState({ icon, title, description, action, dense = false }: EmptyStateProps) {
  return (
    <Stack spacing={1}
      sx={{ alignItems: 'center', py: dense ? 3 : 6, px: 2, textAlign: 'center', color: 'text.secondary' }}
    >
      {icon && (
        <Box sx={{ fontSize: dense ? 32 : 44, display: 'flex', opacity: 0.5 }}>{icon}</Box>
      )}
      <Typography variant={dense ? 'body1' : 'h6'} sx={{ color: 'text.primary' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}
